import { useEffect, useState, useCallback } from 'react';
import { apiRequest } from '../utils/auth';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import { FaThumbtack } from 'react-icons/fa';
import ArticleFormModal from '../components/ArticleFormModal';
import './ArticlesPage.css';

interface CoverImage {
  type: 'articleCoverImage';
  imageKey: string;
  imageUrl?: string;
  videoKey?: string;
  videoUrl?: string;
  thumbnailKey?: string;
  thumbnailUrl?: string;
  title?: string;
}

interface ImageSection {
  type: 'image';
  imageKey: string;
  imageUrl?: string;
  imageKey2?: string;
  imageUrl2?: string;
  title?: string;
}

interface ParagraphSection {
  type: 'paragraph';
  content: string;
}

interface ExtensionCardSection {
  type: 'extensionCard';
  extensionId: string;
  extensionName?: string;
  extensionCreator?: string;
  imageKey?: string;
  imageUrl?: string;
}

interface CreatorCardSection {
  type: 'creatorCard';
  creatorId: string;
  creatorName?: string;
}

interface VideoSection {
  type: 'video';
  videoKey: string;
  videoUrl?: string;
  thumbnailKey?: string;
  thumbnailUrl?: string;
}

type ArticleSection = ImageSection | ParagraphSection | ExtensionCardSection | CreatorCardSection | VideoSection;
type AllSectionTypes = CoverImage | ArticleSection;

interface Article {
  _id: string;
  articleId: string;
  title: string;
  creator?: string;
  sections: AllSectionTypes[];
  isPublished: boolean;
  isPinned?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function ArticlesPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);



  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest<{
        success: boolean;
        data: Article[];
        error?: string;
      }>('/api/admin/articles');

      if (response.success) {
        setArticles(response.data);
      } else {
        setError(response.error || 'Failed to fetch articles');
      }
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to fetch articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const openCreateModal = () => {
    setEditingArticle(null);
    setShowModal(true);
  };

  const openEditModal = (article: Article) => {
    setEditingArticle(article);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingArticle(null);
  };

  const handleSuccess = () => {
    fetchArticles();
  };

  const handleDelete = async (articleId: string) => {
    if (!window.confirm('Are you sure you want to delete this article?')) {
      return;
    }

    try {
      await apiRequest(`/api/admin/articles/${articleId}`, {
        method: 'DELETE',
      });
      fetchArticles();
    } catch (err) {
      console.error('Error deleting article:', err);
      alert('Failed to delete article');
    }
  };

  const handlePin = async (articleId: string) => {
    try {
      await apiRequest(`/api/admin/articles/${articleId}/pin`, {
        method: 'POST',
      });
      fetchArticles();
    } catch (err) {
      console.error('Error pinning article:', err);
      alert('Failed to pin article');
    }
  };

  const handleUnpin = async (articleId: string) => {
    try {
      await apiRequest(`/api/admin/articles/${articleId}/unpin`, {
        method: 'POST',
      });
      fetchArticles();
    } catch (err) {
      console.error('Error unpinning article:', err);
      alert('Failed to unpin article');
    }
  };

  if (loading) {
    return <div className="page articles-page"><div className="loading">Loading articles...</div></div>;
  }

  if (error) {
    return <div className="page articles-page"><div className="error">{error}</div></div>;
  }

  const getArticleStatus = (article: Article): { label: string; className: string } => {
    const now = new Date();
    if (article.startDate && new Date(article.startDate) > now) {
      return { label: 'Scheduled', className: 'scheduled' };
    }
    if (article.endDate && new Date(article.endDate) < now) {
      return { label: 'Expired', className: 'expired' };
    }
    return { label: 'Active', className: 'active' };
  };

  return (
    <div className="page articles-page">
      <div className="page-header">
        <h1>Articles</h1>
        <button className="create-btn" onClick={openCreateModal}>
          <FiPlus /> Create Article
        </button>
      </div>

      <div className="articles-list">
        {articles.length === 0 ? (
          <div className="empty-state">No articles yet. Create your first article!</div>
        ) : (
          articles.map((article) => {
            const coverSection = article.sections[0];
            const coverImageUrl = coverSection?.type === 'articleCoverImage' ? coverSection.imageUrl : undefined;
            const coverVideoUrl = coverSection?.type === 'articleCoverImage' ? coverSection.videoUrl : undefined;
            const coverThumbnailUrl = coverSection?.type === 'articleCoverImage' ? coverSection.thumbnailUrl : undefined;
            const title = coverSection?.type === 'articleCoverImage' ? coverSection.title : article.title;
            const status = getArticleStatus(article);

            return (
              <div key={article._id} className={`article-card${article.isPinned ? ' pinned' : ''}`}>
                {article.isPinned && <div className="pin-badge"><FaThumbtack /> Pinned</div>}
                {coverVideoUrl ? (
                  <img src={coverThumbnailUrl || coverImageUrl || ''} alt={title} className="article-cover" />
                ) : coverImageUrl ? (
                  <img src={coverImageUrl} alt={title} className="article-cover" />
                ) : null}
                <div className="article-info">
                  <h3>{title}</h3>
                  <p className="article-meta">
                    {article.sections.length} section{article.sections.length !== 1 ? 's' : ''} •
                    Created {new Date(article.createdAt).toLocaleDateString()}
                    {article.startDate && (
                      <> • From {new Date(article.startDate).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</>
                    )}
                    {article.endDate && (
                      <> to {new Date(article.endDate).toLocaleString([], { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}</>
                    )}
                  </p>
                  <span className={`article-status-badge ${status.className}`}>{status.label}</span>
                </div>
                <div className="article-actions">
                  {article.isPinned ? (
                    <button className="unpin-btn" onClick={() => handleUnpin(article.articleId)}>
                      <FaThumbtack /> Unpin
                    </button>
                  ) : (
                    <button className="pin-btn" onClick={() => handlePin(article.articleId)}>
                      <FaThumbtack /> Pin
                    </button>
                  )}
                  <button className="edit-btn" onClick={() => openEditModal(article)}>
                    <FiEdit2 /> Edit
                  </button>
                  <button className="delete-btn" onClick={() => handleDelete(article.articleId)}>
                    <FiTrash2 /> Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <ArticleFormModal
        isOpen={showModal}
        editingArticle={editingArticle}
        onClose={closeModal}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
